/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-unused-vars */
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonCurrencyInput, CommonDropdown, OutlinedButton, RegularButton } from 'pg-ui-library';
import { AttentionBallon } from 'src/components/attention-balloon';
import './index.css';
import {
  IBenefitCoverageFixedMaximum,
  IBenefitCoverageRuleMaximum,
  IBenefitCoverageSalaryBased,
  IBenefitCoverageType,
} from 'src/interfaces';
import { Container, ContainerBody, Header, HorizontalLine, RadioContainer } from './styles';

const dropdownRoundOption = [
  { key: '0', label: 'Round', value: 'Round' },
  { key: '1', label: 'No Round', value: 'No Round' },
  { key: '2', label: 'Round Up', value: 'Round Up' },
  { key: '3', label: 'Round Down', value: 'Round Down' },
];

enum CoverageTypeEnum {
  NO_COVERAGE = 'noCoverage',
  FIXED_COVERAGE_AMOUNT = 'fixedCoverage',
  FIXED_COVERAGE_AMOUNT_SALARY_BASED = 'fixedCoverageAmountSalaryBased',
  ELECTED_COVERAGE_AMOUNT_WITH_FIXED_MAXIMUM = 'electedCoverageAmountWithFixedMaximum',
  ELECTED_COVERAGE_AMOUNT_WITH_RULE_BASED_MAXIMUM = 'ElectedCoverageAmountWithRuleBasedMaximum',
}

type ActionType =
  | { type: 'updateCoverageType'; payload: CoverageTypeEnum }
  | { type: 'updateCoverageAmount'; payload: number }
  | { type: 'updateSalaryBased'; payload: Partial<IBenefitCoverageSalaryBased> }
  | { type: 'updateFixedMaximum'; payload: Partial<IBenefitCoverageFixedMaximum> }
  | { type: 'updateRuleBasedMaximum'; payload: Partial<IBenefitCoverageRuleMaximum> };

interface IPriceCoverageForm {
  coverageType: CoverageTypeEnum;
  employeeCoverageAmount: number;
  salaryBased: IBenefitCoverageSalaryBased;
  fixedMaximum: IBenefitCoverageFixedMaximum;
  ruleBasedMaximum: IBenefitCoverageRuleMaximum;
}

const reducer = (state: IPriceCoverageForm, action: ActionType): IPriceCoverageForm => {
  switch (action.type) {
    case 'updateCoverageType':
      return { ...state, coverageType: action.payload };
    case 'updateCoverageAmount':
      return { ...state, employeeCoverageAmount: action.payload };
    case 'updateSalaryBased':
      return { ...state, salaryBased: { ...state.salaryBased, ...action.payload } };
    case 'updateFixedMaximum':
      return { ...state, fixedMaximum: { ...state.fixedMaximum, ...action.payload } };
    case 'updateRuleBasedMaximum':
      return { ...state, ruleBasedMaximum: { ...state.ruleBasedMaximum, ...action.payload } };
    default:
      return state;
  }
};

export const BenefitPriceCoverageForm: React.FC<{
  value: IBenefitCoverageType;
  onChange: (cov: IBenefitCoverageType) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();

  const initialState: IPriceCoverageForm = {
    coverageType: value?.coverageType || CoverageTypeEnum.NO_COVERAGE,
    employeeCoverageAmount: value?.employeeCoverageAmount || 0,
    salaryBased: {
      level: (value?.options as IBenefitCoverageSalaryBased)?.level || 0,
      timesAnnualSalaryUpTo: (value?.options as IBenefitCoverageSalaryBased)?.timesAnnualSalaryUpTo || 0,
      reduceCoverageAmountMaxBy: (value?.options as IBenefitCoverageSalaryBased)?.reduceCoverageAmountMaxBy || 0,
      roundCoverage: (value?.options as IBenefitCoverageSalaryBased)?.roundCoverage || 'Round',
      annualSalaryUpTo: (value?.options as IBenefitCoverageSalaryBased)?.annualSalaryUpTo || 0,
    },
    fixedMaximum: {
      giEnabled: (value?.options as IBenefitCoverageFixedMaximum)?.giEnabled || false,
      giConfig: (value?.options as IBenefitCoverageFixedMaximum)?.giConfig || [{ ageStart: 0, ageEnd: 0, giAmount: 0 }],
      employeeMaxFixed: (value?.options as IBenefitCoverageFixedMaximum)?.employeeMaxFixed || 0,
      roundCoverage: (value?.options as IBenefitCoverageFixedMaximum)?.roundCoverage || 'Round',
      roundToNearFixed: (value?.options as IBenefitCoverageFixedMaximum)?.roundToNearFixed || 0,
      minimumCoverage: (value?.options as IBenefitCoverageFixedMaximum)?.minimumCoverage || 0,
    },
    ruleBasedMaximum: {
      giEnabled: (value?.options as IBenefitCoverageRuleMaximum)?.giEnabled || false,
      giConfig: (value?.options as IBenefitCoverageRuleMaximum)?.giConfig || [{ ageStart: 0, ageEnd: 0, giAmount: 0 }],
      employeeMaxRuleBased: (value?.options as IBenefitCoverageRuleMaximum)?.employeeMaxRuleBased || 0,
      salaryUpToRuleBased: (value?.options as IBenefitCoverageRuleMaximum)?.salaryUpToRuleBased || 0,
      roundCoverage: (value?.options as IBenefitCoverageRuleMaximum)?.roundCoverage || 'Round',
      roundToNearRuleBased: (value?.options as IBenefitCoverageRuleMaximum)?.roundToNearRuleBased || 0,
      minimumCoverage: (value?.options as IBenefitCoverageRuleMaximum)?.minimumCoverage || 0,
    },
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const getOptions = () => {
    if (state.coverageType === CoverageTypeEnum.FIXED_COVERAGE_AMOUNT_SALARY_BASED) {
      return state.salaryBased;
    }
    if (state.coverageType === CoverageTypeEnum.ELECTED_COVERAGE_AMOUNT_WITH_FIXED_MAXIMUM) {
      return state.fixedMaximum;
    }

    if (state.coverageType === CoverageTypeEnum.ELECTED_COVERAGE_AMOUNT_WITH_RULE_BASED_MAXIMUM) {
      return state.ruleBasedMaximum;
    }

    return {};
  };

  useEffect(() => {
    onChange({
      coverageType: state.coverageType,
      employeeCoverageAmount: state.employeeCoverageAmount,
      options: getOptions(),
    });
  }, [state]);

  // Define update functions for each state property
  const updateCoverageType = (newValue: CoverageTypeEnum) => {
    dispatch({ type: 'updateCoverageType', payload: newValue });
  };

  const updateCoverageAmount = (newValue: number) => {
    dispatch({ type: 'updateCoverageAmount', payload: newValue });
  };

  const updateSalaryBased = (newValues: Partial<IBenefitCoverageSalaryBased>) => {
    dispatch({ type: 'updateSalaryBased', payload: newValues });
  };

  const updateFixedMaximum = (newValues: Partial<IBenefitCoverageFixedMaximum>) => {
    dispatch({ type: 'updateFixedMaximum', payload: newValues });
  };

  const updateRuleBasedMaximum = (newValues: Partial<IBenefitCoverageRuleMaximum>) => {
    dispatch({ type: 'updateRuleBasedMaximum', payload: newValues });
  };

  return (
    <Container>
      <Header>
        <div>{t('benefits.coverage.calculation')}</div>
        <HorizontalLine />
      </Header>

      <ContainerBody>
        <RadioContainer>
          <input
            type="radio"
            id="coverage"
            name="coverage"
            value={CoverageTypeEnum.NO_COVERAGE}
            checked={CoverageTypeEnum.NO_COVERAGE === state.coverageType}
            onChange={val => {
              updateCoverageType(val.target.value as CoverageTypeEnum);
            }}
          />
          <label>{t('benefits.coverage.noCoverage')}</label>
        </RadioContainer>

        <RadioContainer>
          <input
            type="radio"
            id="coverage"
            name="coverage"
            value={CoverageTypeEnum.FIXED_COVERAGE_AMOUNT}
            checked={CoverageTypeEnum.FIXED_COVERAGE_AMOUNT === state.coverageType}
            onChange={val => {
              updateCoverageType(val.target.value as CoverageTypeEnum);
            }}
          />
          <label>{t('benefits.coverage.fixedCoverageAmount')}</label>
        </RadioContainer>

        {state.coverageType === CoverageTypeEnum.FIXED_COVERAGE_AMOUNT && (
          <RadioContainer>
            <CommonCurrencyInput
              id="employeeCoverageAmount"
              label="Employee Coverage Amount"
              name="employeeCoverageAmount"
              placeholder="Please enter a number"
              prefix="$"
              allowDecimals={false}
              allowNegativeValue={false}
              value={state.employeeCoverageAmount}
              onValueChange={v => {
                const newValue = Number(v);
                if (Number.isNaN(newValue)) {
                  updateCoverageAmount(0);
                } else {
                  updateCoverageAmount(Number(v));
                }
              }}
            />
          </RadioContainer>
        )}

        <RadioContainer>
          <input
            type="radio"
            id="coverage"
            name="coverage"
            value={CoverageTypeEnum.FIXED_COVERAGE_AMOUNT_SALARY_BASED}
            checked={CoverageTypeEnum.FIXED_COVERAGE_AMOUNT_SALARY_BASED === state.coverageType}
            onChange={val => {
              updateCoverageType(val.target.value as CoverageTypeEnum);
            }}
          />
          <label>{t('benefits.coverage.fixedCoverageSalaryBased')}</label>
        </RadioContainer>

        {state.coverageType === CoverageTypeEnum.FIXED_COVERAGE_AMOUNT_SALARY_BASED && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <CommonCurrencyInput
                id="level"
                label=""
                name="level"
                placeholder=""
                suffix=""
                allowDecimals={false}
                allowNegativeValue={false}
                value={state.salaryBased.level}
                onValueChange={v => {
                  const newValue = Number(v);
                  if (Number.isNaN(newValue)) {
                    updateSalaryBased({ ...state.salaryBased, level: 0 });
                  } else {
                    updateSalaryBased({ ...state.salaryBased, level: Number(v) });
                  }
                }}
                style={{ width: '50px' }}
              />

              <AttentionBallon text="Salary levels must be uniquer and greater than or equal 1" />

              <span style={{ margin: '5px' }}>Times Annual Salary up to</span>

              <CommonCurrencyInput
                id="timesAnnualSalary"
                label=""
                name="timesAnnualSalary"
                placeholder="Please enter a number"
                suffix=""
                allowDecimals={false}
                allowNegativeValue={false}
                value={state.salaryBased.timesAnnualSalaryUpTo}
                onValueChange={v => {
                  const newValue = Number(v);
                  if (Number.isNaN(newValue)) {
                    updateSalaryBased({ ...state.salaryBased, timesAnnualSalaryUpTo: 0 });
                  } else {
                    updateSalaryBased({ ...state.salaryBased, timesAnnualSalaryUpTo: Number(v) });
                  }
                }}
              />

              <span style={{ margin: '5px' }}>Reduce Coverage Amount Max. by</span>

              <CommonCurrencyInput
                id="reduceCoverageAmountMaxBy"
                label=""
                name="reduceCoverageAmountMaxBy"
                placeholder="Please enter a number"
                suffix="x"
                allowDecimals={false}
                maxLength={2}
                allowNegativeValue={false}
                value={state.salaryBased.reduceCoverageAmountMaxBy}
                onValueChange={v => {
                  const newValue = Number(v);
                  if (Number.isNaN(newValue)) {
                    updateSalaryBased({ ...state.salaryBased, reduceCoverageAmountMaxBy: 0 });
                  } else {
                    updateSalaryBased({ ...state.salaryBased, reduceCoverageAmountMaxBy: Number(v) });
                  }
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <CommonDropdown
                label="Round Coverage"
                id="roundCoverage"
                name="roundCoverage"
                setValue={(v: string) => {
                  updateSalaryBased({ ...state.salaryBased, roundCoverage: v });
                }}
                value={state.salaryBased.roundCoverage}
                options={dropdownRoundOption}
              />

              <span style={{ margin: '5px' }}>Times Annual Salary up To</span>

              <CommonCurrencyInput
                id="annualSalaryUpTo"
                label=""
                name="annualSalaryUpTo"
                placeholder="Please enter a number"
                prefix="$"
                allowDecimals={false}
                allowNegativeValue={false}
                value={state.salaryBased.annualSalaryUpTo}
                onValueChange={v => {
                  const newValue = Number(v);
                  if (Number.isNaN(newValue)) {
                    updateSalaryBased({ ...state.salaryBased, annualSalaryUpTo: 0 });
                  } else {
                    updateSalaryBased({ ...state.salaryBased, annualSalaryUpTo: Number(v) });
                  }
                }}
              />
            </div>
          </div>
        )}

        <RadioContainer>
          <input
            type="radio"
            id="coverage"
            name="coverage"
            value={CoverageTypeEnum.ELECTED_COVERAGE_AMOUNT_WITH_FIXED_MAXIMUM}
            checked={CoverageTypeEnum.ELECTED_COVERAGE_AMOUNT_WITH_FIXED_MAXIMUM === state.coverageType}
            onChange={val => {
              updateCoverageType(val.target.value as CoverageTypeEnum);
            }}
          />
          <label>{t('benefits.coverage.electedCoverageAmountWithFixMax')}</label>
        </RadioContainer>

        {state.coverageType === CoverageTypeEnum.ELECTED_COVERAGE_AMOUNT_WITH_FIXED_MAXIMUM && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div
                style={{
                  width: '40vw',
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                {t('benefits.coverage.enableDisabledGi')}
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={state.fixedMaximum.giEnabled}
                    onChange={e => {
                      updateFixedMaximum({
                        ...state.fixedMaximum,
                        giEnabled: e.target.checked,
                        giConfig: [{ ageStart: 0, ageEnd: 0, giAmount: 0 }], // initial empty value
                      });
                    }}
                  />
                  <span className="slider round" />
                </label>
              </div>
              {state.fixedMaximum.giEnabled ? (
                state.fixedMaximum.giConfig.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div style={{ display: 'flex', gap: '10px' }} key={index}>
                    <CommonCurrencyInput
                      id="ageStart"
                      label={index === 0 ? 'Age Start' : ''}
                      name="ageStart"
                      placeholder="Please enter a number"
                      prefix=""
                      max={100}
                      maxLength={3}
                      allowDecimals={false}
                      allowNegativeValue={false}
                      value={item.ageStart}
                      groupSeparator=","
                      decimalSeparator="."
                      onValueChange={v => {
                        if (Number.isNaN(v)) {
                          const newValue = state.fixedMaximum.giConfig;
                          newValue[index].ageStart = 0;
                          updateFixedMaximum({ ...state.fixedMaximum, giConfig: newValue });
                        } else {
                          const newValue = state.fixedMaximum.giConfig;
                          newValue[index].ageStart = v as string;
                          updateFixedMaximum({ ...state.fixedMaximum, giConfig: newValue });
                        }
                      }}
                    />
                    <CommonCurrencyInput
                      id="ageEnd"
                      label={index === 0 ? 'Age End' : ''}
                      name="ageEnd"
                      placeholder="Please enter a number"
                      prefix=""
                      allowDecimals={false}
                      allowNegativeValue={false}
                      max={100}
                      maxLength={3}
                      value={item.ageEnd}
                      groupSeparator=","
                      decimalSeparator="."
                      onValueChange={v => {
                        if (Number.isNaN(v)) {
                          const newValue = state.fixedMaximum.giConfig;
                          newValue[index].ageEnd = 0;
                          updateFixedMaximum({ ...state.fixedMaximum, giConfig: newValue });
                        } else {
                          const newValue = state.fixedMaximum.giConfig;
                          newValue[index].ageEnd = v as string;
                          updateFixedMaximum({ ...state.fixedMaximum, giConfig: newValue });
                        }
                      }}
                    />
                    <CommonCurrencyInput
                      id="giAmount"
                      label={index === 0 ? 'GI Amount' : ''}
                      name="giAmount"
                      placeholder="Please enter a number"
                      prefix="$"
                      allowDecimals={false}
                      allowNegativeValue={false}
                      value={item.giAmount}
                      onValueChange={v => {
                        if (Number.isNaN(v)) {
                          const newValue = state.fixedMaximum.giConfig;
                          newValue[index].giAmount = 0;
                          updateFixedMaximum({ ...state.fixedMaximum, giConfig: newValue });
                        } else {
                          const newValue = state.fixedMaximum.giConfig;
                          newValue[index].giAmount = v as string;
                          updateFixedMaximum({ ...state.fixedMaximum, giConfig: newValue });
                        }
                      }}
                    />

                    {index !== 0 && (
                      <OutlinedButton
                        style={{ minWidth: '108px' }}
                        onClick={() => {
                          const newValue = state.fixedMaximum.giConfig;
                          newValue.splice(index, 1);
                          updateFixedMaximum({ ...state.fixedMaximum, giConfig: newValue });
                        }}
                      >
                        {t('global.form.delete')}
                      </OutlinedButton>
                    )}
                    {index === 0 && (
                      <RegularButton
                        style={{ marginTop: '28px' }}
                        onClick={() => {
                          const newValue = state.fixedMaximum.giConfig;
                          newValue.push({ ageStart: 0, ageEnd: 0, giAmount: 0 });
                          updateFixedMaximum({ ...state.fixedMaximum, giConfig: newValue });
                        }}
                      >
                        {t('global.form.add')}
                      </RegularButton>
                    )}
                  </div>
                ))
              ) : (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <></>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '30px' }}>
              <CommonCurrencyInput
                id="employeeMaxFixed"
                label="Employee Max"
                name="employeeMaxFixed"
                placeholder="Please enter a number"
                prefix="$"
                allowDecimals={false}
                allowNegativeValue={false}
                value={state.fixedMaximum.employeeMaxFixed}
                onValueChange={v => {
                  const newValue = Number(v);
                  if (Number.isNaN(newValue)) {
                    updateFixedMaximum({ ...state.fixedMaximum, employeeMaxFixed: 0 });
                  } else {
                    updateFixedMaximum({ ...state.fixedMaximum, employeeMaxFixed: Number(v) });
                  }
                }}
              />

              <CommonCurrencyInput
                id="minimumCoverage"
                label="Minimum Coverage"
                name="minimumCoverage"
                placeholder="Please enter a number"
                prefix="$"
                allowDecimals={false}
                allowNegativeValue={false}
                value={state.fixedMaximum.minimumCoverage}
                onValueChange={v => {
                  const newValue = Number(v);
                  if (Number.isNaN(newValue)) {
                    updateFixedMaximum({ ...state.fixedMaximum, minimumCoverage: 0 });
                  } else {
                    updateFixedMaximum({ ...state.fixedMaximum, minimumCoverage: Number(v) });
                  }
                }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <CommonDropdown
                label="Round Coverage"
                id="roundCoverageFixedMax"
                name="roundCoverageFixedMax"
                setValue={(v: string) => {
                  updateFixedMaximum({ ...state.fixedMaximum, roundCoverage: v });
                }}
                value={state.fixedMaximum.roundCoverage}
                options={dropdownRoundOption}
              />

              <span style={{ margin: '5px' }}>{t('benefits.coverage.toTheNearest')}</span>

              <CommonCurrencyInput
                id="roundToNearFixed"
                label=""
                name="roundToNearFixed"
                placeholder="Please enter a number"
                prefix="$"
                allowDecimals={false}
                allowNegativeValue={false}
                value={state.fixedMaximum.roundToNearFixed}
                onValueChange={v => {
                  const newValue = Number(v);
                  if (Number.isNaN(newValue)) {
                    updateFixedMaximum({ ...state.fixedMaximum, roundToNearFixed: 0 });
                  } else {
                    updateFixedMaximum({ ...state.fixedMaximum, roundToNearFixed: Number(v) });
                  }
                }}
              />
            </div>
          </div>
        )}

        <RadioContainer>
          <input
            type="radio"
            id="coverage"
            name="coverage"
            value={CoverageTypeEnum.ELECTED_COVERAGE_AMOUNT_WITH_RULE_BASED_MAXIMUM}
            checked={CoverageTypeEnum.ELECTED_COVERAGE_AMOUNT_WITH_RULE_BASED_MAXIMUM === state.coverageType}
            onChange={val => {
              updateCoverageType(val.target.value as CoverageTypeEnum);
            }}
          />
          <label>{t('benefits.coverage.electedCoverageAmountRuleBasedMax')}</label>
        </RadioContainer>

        {state.coverageType === CoverageTypeEnum.ELECTED_COVERAGE_AMOUNT_WITH_RULE_BASED_MAXIMUM && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div
                style={{
                  width: '40vw',
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                {t('benefits.coverage.enableDisabledGi')}
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={state.ruleBasedMaximum.giEnabled}
                    onChange={e => {
                      updateRuleBasedMaximum({
                        ...state.ruleBasedMaximum,
                        giEnabled: e.target.checked,
                        giConfig: [{ ageStart: 0, ageEnd: 0, giAmount: 0 }], // initial empty value
                      });
                    }}
                  />
                  <span className="slider round" />
                </label>
              </div>
              {state.ruleBasedMaximum.giEnabled ? (
                state.ruleBasedMaximum.giConfig.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div style={{ display: 'flex', gap: '10px' }} key={index}>
                    <CommonCurrencyInput
                      id="ageStart"
                      label={index === 0 ? 'Age Start' : ''}
                      name="ageStart"
                      placeholder="Please enter a number"
                      prefix=""
                      max={100}
                      maxLength={3}
                      allowDecimals={false}
                      allowNegativeValue={false}
                      value={item.ageStart}
                      groupSeparator=","
                      decimalSeparator="."
                      onValueChange={v => {
                        if (Number.isNaN(v)) {
                          const newValue = state.ruleBasedMaximum.giConfig;
                          newValue[index].ageStart = 0;
                          updateRuleBasedMaximum({ ...state.ruleBasedMaximum, giConfig: newValue });
                        } else {
                          const newValue = state.ruleBasedMaximum.giConfig;
                          newValue[index].ageStart = v as string;
                          updateRuleBasedMaximum({ ...state.ruleBasedMaximum, giConfig: newValue });
                        }
                      }}
                    />
                    <CommonCurrencyInput
                      id="ageEnd"
                      label={index === 0 ? 'Age End' : ''}
                      name="ageEnd"
                      placeholder="Please enter a number"
                      prefix=""
                      allowDecimals={false}
                      allowNegativeValue={false}
                      max={100}
                      maxLength={3}
                      value={item.ageEnd}
                      groupSeparator=","
                      decimalSeparator="."
                      onValueChange={v => {
                        if (Number.isNaN(v)) {
                          const newValue = state.ruleBasedMaximum.giConfig;
                          newValue[index].ageEnd = 0;
                          updateRuleBasedMaximum({ ...state.ruleBasedMaximum, giConfig: newValue });
                        } else {
                          const newValue = state.ruleBasedMaximum.giConfig;
                          newValue[index].ageEnd = v as string;
                          updateRuleBasedMaximum({ ...state.ruleBasedMaximum, giConfig: newValue });
                        }
                      }}
                    />
                    <CommonCurrencyInput
                      id="giAmount"
                      label={index === 0 ? 'GI Amount' : ''}
                      name="giAmount"
                      placeholder="Please enter a number"
                      prefix="$"
                      allowDecimals={false}
                      allowNegativeValue={false}
                      value={item.giAmount}
                      onValueChange={v => {
                        if (Number.isNaN(v)) {
                          const newValue = state.ruleBasedMaximum.giConfig;
                          newValue[index].giAmount = 0;
                          updateRuleBasedMaximum({ ...state.ruleBasedMaximum, giConfig: newValue });
                        } else {
                          const newValue = state.ruleBasedMaximum.giConfig;
                          newValue[index].giAmount = v as string;
                          updateRuleBasedMaximum({ ...state.ruleBasedMaximum, giConfig: newValue });
                        }
                      }}
                    />

                    {index !== 0 && (
                      <OutlinedButton
                        style={{ minWidth: '108px' }}
                        onClick={() => {
                          const newValue = state.ruleBasedMaximum.giConfig;
                          newValue.splice(index, 1);
                          updateRuleBasedMaximum({ ...state.ruleBasedMaximum, giConfig: newValue });
                        }}
                      >
                        {t('global.form.delete')}
                      </OutlinedButton>
                    )}
                    {index === 0 && (
                      <RegularButton
                        style={{ marginTop: '28px' }}
                        onClick={() => {
                          const newValue = state.ruleBasedMaximum.giConfig;
                          newValue.push({ ageStart: 0, ageEnd: 0, giAmount: 0 });
                          updateRuleBasedMaximum({ ...state.ruleBasedMaximum, giConfig: newValue });
                        }}
                      >
                        {t('global.form.add')}
                      </RegularButton>
                    )}
                  </div>
                ))
              ) : (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <></>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <CommonCurrencyInput
                id="employeeMaxRuleBased"
                label="Employee Max"
                name="employeeMaxRuleBased"
                placeholder="Please enter a number"
                suffix="%"
                allowDecimals={false}
                allowNegativeValue={false}
                value={state.ruleBasedMaximum.employeeMaxRuleBased}
                onValueChange={v => {
                  const newValue = Number(v);
                  if (Number.isNaN(newValue)) {
                    updateRuleBasedMaximum({ ...state.ruleBasedMaximum, employeeMaxRuleBased: 0 });
                  } else {
                    updateRuleBasedMaximum({ ...state.ruleBasedMaximum, employeeMaxRuleBased: Number(v) });
                  }
                }}
              />

              <span style={{ margin: '5px' }}>{t('benefits.coverage.upToannualSalary')}</span>

              <CommonCurrencyInput
                id="salaryUpToRuleBased"
                label=""
                name="salaryUpToRuleBased"
                placeholder="Please enter a number"
                prefix="$"
                allowDecimals={false}
                allowNegativeValue={false}
                value={state.ruleBasedMaximum.salaryUpToRuleBased}
                onValueChange={v => {
                  const newValue = Number(v);
                  if (Number.isNaN(newValue)) {
                    updateRuleBasedMaximum({ ...state.ruleBasedMaximum, salaryUpToRuleBased: 0 });
                  } else {
                    updateRuleBasedMaximum({ ...state.ruleBasedMaximum, salaryUpToRuleBased: Number(v) });
                  }
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <CommonDropdown
                label="Round Coverage"
                id="roundCoverageRuleMax"
                name="roundCoverageRuleMax"
                setValue={(v: string) => {
                  updateRuleBasedMaximum({ ...state.ruleBasedMaximum, roundCoverage: v });
                }}
                value={state.ruleBasedMaximum.roundCoverage}
                options={dropdownRoundOption}
              />

              <span style={{ margin: '5px' }}>{t('benefits.coverage.toTheNearest')}</span>

              <CommonCurrencyInput
                id="roundToNearRuleBased"
                label=""
                name="roundToNearRuleBased"
                placeholder="Please enter a number"
                prefix="$"
                allowDecimals={false}
                allowNegativeValue={false}
                value={state.ruleBasedMaximum.roundToNearRuleBased}
                onValueChange={v => {
                  const newValue = Number(v);
                  if (Number.isNaN(newValue)) {
                    updateRuleBasedMaximum({ ...state.ruleBasedMaximum, roundToNearRuleBased: 0 });
                  } else {
                    updateRuleBasedMaximum({ ...state.ruleBasedMaximum, roundToNearRuleBased: Number(v) });
                  }
                }}
              />
            </div>
            <CommonCurrencyInput
              id="minimumCoverage"
              label="Minimum Coverage"
              name="minimumCoverage"
              placeholder="Please enter a number"
              prefix="$"
              allowDecimals={false}
              allowNegativeValue={false}
              value={state.ruleBasedMaximum.minimumCoverage}
              onValueChange={v => {
                const newValue = Number(v);
                if (Number.isNaN(newValue)) {
                  updateRuleBasedMaximum({ ...state.ruleBasedMaximum, minimumCoverage: 0 });
                } else {
                  updateRuleBasedMaximum({ ...state.ruleBasedMaximum, minimumCoverage: Number(v) });
                }
              }}
            />
          </div>
        )}
      </ContainerBody>
    </Container>
  );
};
